import React, { useEffect, useRef } from 'react';
import './TableMenu.css';

/* The table menu, in the top bar beside history and rankings.
 *
 * These controls replaced a fixed 56px footer. They are touched a few times a
 * session while the table is what you look at continuously, so they do not
 * earn permanent screen space — no poker client spends a band on them.
 *
 * What stays outside the menu: your hand's name, which you read every hand;
 * the winner banner, which is transient; and Start Hand, which is the one
 * thing an idle table is for.
 */
export default function TableMenu({ open, onToggle, items }) {
  const ref = useRef(null);

  // Click away or press Escape to dismiss, the way a menu should behave.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onToggle(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') onToggle(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onToggle]);

  const visible = items.filter(Boolean);

  return (
    <div className="tm" ref={ref}>
      <button
        className={`tm-trigger ${open ? 'open' : ''}`}
        onClick={() => onToggle(!open)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Table options"
        title="Table options"
      >
        {/* Three rules rather than a hamburger: it reads as a stack of tiles,
            which is what this table is made of. */}
        <span className="tm-glyph" aria-hidden="true" />
      </button>

      {open && (
        <div className="tm-panel" role="menu">
          {visible.map((item, i) => {
            if (item.divider) return <div key={`d${i}`} className="tm-divider" />;
            return (
              <button
                key={item.label}
                role="menuitem"
                className={`tm-item ${item.tone ?? ''} ${item.on ? 'on' : ''}`}
                disabled={item.disabled}
                onClick={() => { item.onSelect(); if (!item.keepOpen) onToggle(false); }}
              >
                <span className="tm-label">{item.label}</span>
                {item.note && <span className="tm-note">{item.note}</span>}
                {item.on && <span className="tm-check" aria-hidden="true">·</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
